import { forwardRef, memo, Ref, useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '~/commonComponents/Box';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { useToggle } from '~/hooks';
import InfoIcon from '~/images/icons/Info';
import StartClockIcon from '~/images/start-clock.svg';
import WarningIcon from '~/images/warning-icon.svg';
import { Colors } from '~/themes/colors';
import Button from '../../Button';
import { Center } from '../../Center';
import { Column } from '../../Column';
import { InputSelect, ISelectOption } from '../../InputSelect';
import { Row } from '../../Row';
import { Text } from '../../Text';
import { Field, Title, Value } from '../Field';
import { IInputJobProps } from '../Types';
import { ErrorType, TInternalValue } from './Types';
import { useGetDefaultTimeRange } from './useGetDefaultTimeRange';
import { useTimeLimitValidation } from './useTimeLimitValidation';
import { useTimeOrderValidation } from './useTimeOrderValidation';
import { useUnlikelyShiftValidation } from './useUnlikelyShiftValidation';
import { factoryTimeOptions } from './Utils';

const TimeOptions: ISelectOption[] = factoryTimeOptions();

/**
 * Props for the Hours input component.
 * Extends IInputJobProps but omits single-value props in favor of arrival/departure pairs.
 */
interface IHoursProps extends Omit<IInputJobProps<string>, 'defaultValue' | 'onChange' | 'value'> {
  /** Default arrival time value (e.g., "08:00 am") */
  defaultArrivalValue?: string;
  /** Default departure time value (e.g., "05:00 pm") */
  defaultDepartureValue?: string;
  /** Override the guidance text shown when component is open */
  guidanceText?: string | React.ReactNode;
  /** Configurable time limit in minutes (e.g., 240 for 4-hour onsite interviews) */
  timeLimitInMinutes?: number;
  /** Controlled arrival time value */
  valueArrival?: string;
  /** Controlled departure time value */
  valueDeparture?: string;
  /** Callback fired when arrival or departure time changes */
  onChange?(arrival: string, departure: string): void;
}

const Component = (
  {
    defaultArrivalValue,
    defaultDepartureValue,
    guidanceText,
    isDisabled,
    isOpen,
    timeLimitInMinutes,
    valueArrival: controlledArrivalValue,
    valueDeparture: controlledDepartureValue,
    onChange,
    onToggle,
  }: IHoursProps,
  ref: Ref<HTMLDivElement>,
) => {
  const computedDefaultDeparture = useGetDefaultTimeRange({
    defaultArrivalValue,
    defaultDepartureValue,
    timeLimitInMinutes,
  });
  const { arrival: DEFAULT_ARRIVAL, departure: DEFAULT_DEPARTURE } = computedDefaultDeparture;

  const [internalValue, setInternalValue] = useState<TInternalValue>(computedDefaultDeparture);

  const [isShowError, controls] = useToggle();
  const errorTypeRef = useRef<ErrorType | undefined>(undefined);

  const isControlled =
    controlledArrivalValue !== undefined && controlledDepartureValue !== undefined;
  const isExceedsTimeLimitErr = errorTypeRef.current === ErrorType.ExceedsTimeLimit;

  const confirmButtonDisabled = isDisabled || isExceedsTimeLimitErr;

  useEffect(() => {
    if (isControlled) {
      setInternalValue({
        arrival: controlledArrivalValue || DEFAULT_ARRIVAL,
        departure: controlledDepartureValue || DEFAULT_DEPARTURE,
      });
    }
  }, [
    controlledArrivalValue,
    controlledDepartureValue,
    isControlled,
    DEFAULT_ARRIVAL,
    DEFAULT_DEPARTURE,
  ]);

  const wasControlledRef = useRef(isControlled);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && wasControlledRef.current !== isControlled) {
      console.warn(
        `Warning: Hours component is changing from ${wasControlledRef.current ? 'controlled' : 'uncontrolled'} to ${isControlled ? 'controlled' : 'uncontrolled'}. ` +
          'Elements should not switch from controlled to uncontrolled (or vice versa).',
      );
    }
    wasControlledRef.current = isControlled;
  }, [isControlled]);

  const { doValidate: doValidateTimeOrder } = useTimeOrderValidation({ controls });

  const { doValidate: doValidateTimeLimitRestriction, message: errMsgTimeLimitRestriction } =
    useTimeLimitValidation({
      controls,
      internalValues: internalValue,
      timeLimitInMinutes,
    });

  const { doValidate: doValidateUnlikelyShift } = useUnlikelyShiftValidation({ controls });

  const doUpdateValue = useCallback(
    (newArrival: string, newDeparture: string) => {
      controls.setOff();

      setInternalValue({
        arrival: newArrival,
        departure: newDeparture,
      });

      // FIRST: Check time order validation (blocking)
      const errTimeOrder = doValidateTimeOrder(newArrival, newDeparture);
      if (errTimeOrder) {
        errorTypeRef.current = errTimeOrder;
        return;
      }

      // SECOND: Check time limit validation (if provided) (blocking)
      const errTimeLimitRestriction = doValidateTimeLimitRestriction(newArrival, newDeparture);
      if (errTimeLimitRestriction) {
        errorTypeRef.current = errTimeLimitRestriction;
        return;
      }

      // THIRD: Check unlikely shift validation (warning)
      const errUnlikelyShift = doValidateUnlikelyShift(newArrival, newDeparture);
      if (errUnlikelyShift) {
        errorTypeRef.current = errUnlikelyShift;
        return;
      }

      onToggle?.();
      onChange?.(newArrival, newDeparture);
    },
    [
      controls,
      doValidateTimeOrder,
      doValidateTimeLimitRestriction,
      doValidateUnlikelyShift,
      onChange,
      onToggle,
    ],
  );

  const handleSelectValueChange = (key: keyof TInternalValue) => (value: string) =>
    setInternalValue((prev) => ({ ...prev, [key]: value }));

  const handleValueChange = useCallback(() => {
    doUpdateValue(
      internalValue.arrival || DEFAULT_ARRIVAL,
      internalValue.departure || DEFAULT_DEPARTURE,
    );
  }, [
    DEFAULT_ARRIVAL,
    DEFAULT_DEPARTURE,
    doUpdateValue,
    internalValue.arrival,
    internalValue.departure,
  ]);

  useEffect(() => {
    if (internalValue.arrival && internalValue.departure) {
      const errTimeLimitRestriction = doValidateTimeLimitRestriction(
        internalValue.arrival,
        internalValue.departure,
      );
      if (errTimeLimitRestriction) {
        errorTypeRef.current = errTimeLimitRestriction;
      } else if (errorTypeRef.current === ErrorType.ExceedsTimeLimit) {
        errorTypeRef.current = undefined;
      }
    }
  }, [internalValue, doValidateTimeLimitRestriction]);

  const ValueComponent = useCallback(() => {
    const currentArrivalValue = isControlled ? controlledArrivalValue : defaultArrivalValue;
    const currentDepartureValue = isControlled ? controlledDepartureValue : defaultDepartureValue;
    const hasValue = currentArrivalValue && currentDepartureValue;
    const displayText =
      !isOpen && hasValue
        ? `${currentArrivalValue} - ${currentDepartureValue}`
        : 'Arrival/Departure';

    const color = isOpen || !hasValue ? Colors.neutral_300 : Colors.neutral_600;

    return <Value text={displayText} style={{ color }} />;
  }, [
    controlledArrivalValue,
    controlledDepartureValue,
    defaultArrivalValue,
    defaultDepartureValue,
    isControlled,
    isOpen,
  ]);

  return (
    <>
      <Field
        ComponentProps={{
          Button: {
            'data-cy': `input-job.hours.trigger`,
            'data-testid': `input-job.hours.trigger`,
          },
          Container: { 'data-cy': `input-job.hours`, 'data-testid': `input-job.hours` },
        }}
        TitleComponent={TitleComponent}
        ValueComponent={ValueComponent}
        IconSrc={StartClockIcon}
        isDisabled={isDisabled}
        isOpen={isOpen}
        ref={ref}
        onToggle={onToggle}
      >
        <Center flexDirection="column" gap={32}>
          <Text color={Colors.neutral_500} italic textAlign="center" width={"80%"}>
            {guidanceText ?? (
              <>
                Setting “Arrival” 15-30 minutes before the first patient allows professionals to
                <br />
                become familiar with your office and be ready to work.
              </>
            )}
          </Text>
          {isShowError && isExceedsTimeLimitErr && timeLimitInMinutes && (
            <Row alignItems="center" gap={2}>
              <Box transform="rotate(180deg)">
                <InfoIcon color={Colors.error_500} />
              </Box>
              <Text color={Colors.error_500}>{errMsgTimeLimitRestriction}</Text>
            </Row>
          )}
          <Row alignItems="center" gap={20} paddingInline={60}>
            <Row alignItems="center" gap={12}>
              <Title text="Arrival:" />
              <InputSelect
                dataCy={`input-job.hours.arrival`}
                dataTestId={`input-job.hours.arrival`}
                error={isShowError && isExceedsTimeLimitErr}
                options={TimeOptions}
                setValue={handleSelectValueChange('arrival')}
                value={internalValue.arrival || DEFAULT_ARRIVAL}
              />
            </Row>
            <Text>/</Text>
            <Row alignItems="center" gap={12}>
              <Title text="Departure:" />
              <InputSelect
                dataCy={`input-job.hours.departure`}
                dataTestId={`input-job.hours.departure`}
                error={isShowError && isExceedsTimeLimitErr}
                options={TimeOptions}
                setValue={handleSelectValueChange('departure')}
                value={internalValue.departure || DEFAULT_DEPARTURE}
              />
            </Row>
          </Row>

          <Button
            color="secondary"
            data-testId={`input-job.hours.confirm`}
            dataCy={`input-job.hours.confirm`}
            disabled={confirmButtonDisabled}
            text="Confirm Hours"
            style={{
              height: 48,
              width: 180,
              backgroundColor: confirmButtonDisabled ? Colors.secondary_200 : Colors.secondary_500,
            }}
            onClick={handleValueChange}
          />
        </Center>
      </Field>
      {isShowError && errorTypeRef.current === ErrorType.IncorrectTimeOrder && (
        // @ts-expect-error - ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => <img src={WarningIcon} alt="Warning" style={{ marginBottom: 21 }} />}
          title="Sorry!"
          description="Arrival Time cannot be after Departure Time"
          rightButtonAction={() => {
            errorTypeRef.current = undefined;
            controls.setOff();
          }}
          rightButtonText="Close"
        />
      )}
      {isShowError && errorTypeRef.current === ErrorType.UnlikelyShifts && (
        // @ts-expect-error - ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => <img src={WarningIcon} alt="Warning" style={{ marginBottom: 21 }} />}
          title="Please Review Shift Time"
          description={
            <>
              <Text color="neutral_500" textAlign="center">
                The hours you&apos;ve entered appear to be outside the usual range. If they&apos;re
                accurate, click confirm. Otherwise, you can go back to make edits.
              </Text>
              <Column marginTop={18} gap={10}>
                <Row justifyContent="space-between" marginHorizontal={65}>
                  <Text color="neutral_500" bold>
                    Arrival Time:
                  </Text>
                  <Text color="neutral_500">{internalValue?.arrival}</Text>
                </Row>
                <Row justifyContent="space-between" marginHorizontal={65}>
                  <Text color="neutral_500" bold>
                    Departure Time:
                  </Text>
                  <Text color="neutral_500">{internalValue?.departure}</Text>
                </Row>
              </Column>
            </>
          }
          leftButtonText="Go Back"
          leftButtonAction={() => {
            errorTypeRef.current = undefined;
            controls.setOff();
          }}
          rightButtonAction={() => {
            onToggle?.();
            onChange?.(internalValue.arrival, internalValue.departure);
            controls.setOff();
          }}
          rightButtonText="Confirm"
        />
      )}
    </>
  );
};

const TitleComponent = () => (
  <Row alignItems="center">
    <Title text="Hours:" />
    <Center
      backgroundColor={Colors.neutral_300}
      borderRadius={100}
      height={16}
      marginLeft={8}
      padding="0 4px"
      width={24}
    >
      <Text bold color={Colors.white} fontSize={12}>
        EDT
      </Text>
    </Center>
  </Row>
);

/**
 * Hours input component for selecting arrival and departure times in job postings.
 *
 * Features:
 * - Time selection in 15-minute intervals from 12:00 am to 11:45 pm
 * - Supports both controlled and uncontrolled modes
 * - Displays times in 12-hour format with am/pm indicators
 * - Shows timezone badge (EDT)
 * - Includes helpful guidance text about arrival time recommendations
 * - Automatic field closure after confirmation with smooth UX transition
 *
 * @example
 * ```tsx
 * // Uncontrolled usage
 * <Hours
 *   defaultArrivalValue="08:00 am"
 *   defaultDepartureValue="05:00 pm"
 *   onChange={(arrival, departure) => console.log(arrival, departure)}
 * />
 *
 * // Controlled usage
 * <Hours
 *   valueArrival={arrivalTime}
 *   valueDeparture={departureTime}
 *   onChange={handleTimeChange}
 *   isOpen={isFieldOpen}
 *   onToggle={handleToggle}
 * />
 * ```
 */
export const Hours = memo(forwardRef(Component)) as <T = HTMLDivElement>(
  props: IHoursProps & { ref?: Ref<T> },
) => ReturnType<typeof Component>;
